import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { onlineManager, QueryClient } from "@tanstack/react-query";
import { persistQueryClientRestore, persistQueryClientSubscribe } from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

import { dropPeopleOnScopeChange } from "./cache-policy.ts";

import type { UserId } from "@estate-platform/domain/ids";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";

const maxAge = 7 * 24 * 60 * 60 * 1000;

export interface Cache {
  readonly queryClient: QueryClient;
  readonly restore: (userId: UserId) => Promise<void>;
  readonly clear: () => Promise<void>;
}

// WHY: the disk policy is needed only once a session is restored, so it loads beside the first paint instead of in it.
const diskPolicy = import("./disk-policy.ts");

const persisterFor = (userId: UserId, forDisk: (client: PersistedClient) => PersistedClient): Persister =>
  createAsyncStoragePersister({
    storage: { getItem: async (key) => get<string>(key), setItem: set, removeItem: del },
    key: `estate-platform:query-cache:${userId}`,
    serialize: (client) => JSON.stringify(forDisk(client)),
  });

interface Session {
  readonly userId: UserId;
  readonly persister: Persister;
  stop: (() => void) | null;
}

export const createCache = (release: string): Cache => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, gcTime: maxAge, networkMode: "offlineFirst", retry: 1 },
      mutations: { networkMode: "always", retry: false },
    },
  });
  dropPeopleOnScopeChange(queryClient);
  onlineManager.subscribe((isOnline) => {
    if (isOnline) {
      void queryClient.invalidateQueries();
    }
  });
  let session: Session | null = null;

  const end = async (ended: Session | null): Promise<void> => {
    ended?.stop?.();
    queryClient.clear();
    await ended?.persister.removeClient();
  };

  const clear = async (): Promise<void> => {
    const ended = session;
    session = null;
    await end(ended);
  };

  const restore = async (userId: UserId): Promise<void> => {
    const { forDisk } = await diskPolicy;
    if (session?.userId === userId) {
      return;
    }
    const previous = session;
    const next: Session = { userId, persister: persisterFor(userId, forDisk), stop: null };
    session = next;
    await end(previous);
    await persistQueryClientRestore({ queryClient, persister: next.persister, maxAge, buster: release });
    next.stop = persistQueryClientSubscribe({ queryClient, persister: next.persister, buster: release });
  };

  return { queryClient, restore, clear };
};
